import React, { memo, useState } from "react";
import MarkdownComponent from "components/markdown";
import { Reference } from "../types";
import { Dialog } from "components/dialog";
import { FileSystemItem } from "types";
import FileView from "views/library/file-or-folder-view/components/FileView";
import { useTranslation } from "react-i18next";

interface SingleMessageProps {
  message: string;
  time: string;
  extra?: string;
  timecolor?: string;
  isUser?: boolean;
  references?: Reference[];
  isLast: boolean;
}

const formatTime = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const SingleMessage: React.FC<SingleMessageProps> = memo(
  ({
    message,
    time,
    extra = "",
    timecolor = "",
    isUser = false,
    references = [],
    isLast = false,
  }) => {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogRef, setDialogRef] = useState<Reference | null>(null);
    const [dialogPage, setDialogPage] = useState<number | null>(null);

    const [fileToShow, setFileToShow] = useState<FileSystemItem | null>(null);
    console.log(references);
    // Open dialog for a reference and page
    const handleOpenDialog = async (ref: Reference, page: number) => {
      setDialogRef(ref);
      setDialogPage(page);
      setDialogOpen(true);
      setFileToShow({
        id: ref.id,
        name: ref.name,
        created_at: ref.metadata?.uploaded_at || "",
        updated_at: ref.metadata?.uploaded_at || "",
        agent_document_id: undefined,
        parent_id: null,
        workspace_id: "",
        path: ref.path,
        document_metadata: {
          file_size: ref.metadata?.file_size || 0,
          file_type: ref.metadata?.file_type || "pdf",
          original_filename: ref.metadata?.original_filename || "",
          uploaded_at: ref.metadata?.uploaded_at || "",
        },
        type: "Document",
      });
    };

    // Close dialog
    const handleCloseDialog = () => {
      setDialogOpen(false);
      setTimeout(() => {
        setDialogRef(null);
        setDialogPage(null);
        setFileToShow(null);
      }, 200);
    };

    return (
      <>
        <div
          className={`mb-4 flex ${isUser ? "justify-end" : "justify-start"}`}
        >
          <div
            className={`parent max-w-[80%] p-3 ${extra} ${
              isUser
                ? "rounded-lg rounded-br-none bg-blue-500 dark:bg-purple-900"
                : "rounded-lg rounded-bl-none bg-gray-100 dark:bg-gray-800"
            }`}
          >
            {isLast && !isUser ? (
              <MarkdownComponent isUser={isUser} isTypeWriting text={message} />
            ) : (
              <MarkdownComponent isUser={isUser} text={message} />
            )}
            {references && references.length > 0 && (
              <div className="mt-2">
                <p className="font-medium dark:text-gray-200">
                  {t("library.references")}:
                </p>
                <ul className="list-disc pl-10">
                  {references.map((ref) => (
                    <li key={ref.id} className="dark:text-gray-200">
                      {ref.name}{" "}
                      {ref.pages &&
                        ref.pages.map((p, idx) => (
                          <span
                            key={idx}
                            className="ml-2 inline-flex cursor-pointer items-center gap-1 "
                            onClick={() => handleOpenDialog(ref, p.number)}
                            title={`${t("library.viewPage")} ${p.number}`}
                          >
                            [
                            <span className="text-blue-600 hover:underline dark:text-blue-400">
                              {p.number}
                            </span>
                            ]
                          </span>
                        ))}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p
              className={`mt-1 text-xs ${timecolor} ${
                !timecolor && "text-gray-500 dark:text-gray-400"
              }`}
            >
              {formatTime(time)}
            </p>
          </div>
        </div>
        <Dialog
          isOpen={dialogOpen && !!dialogRef && !!dialogPage}
          setIsOpen={setDialogOpen}
          onClose={handleCloseDialog}
          header={t("library.documentInfo")}
          headerClassName="sm:px-4 px-2 py-3 !border-none !mb-0    "
          className="!h-screen !max-h-[100vh] !w-[90vw] !max-w-[90vw] !p-0 "
        >
          <FileView file={fileToShow} page={dialogPage} />
        </Dialog>
      </>
    );
  }
);
SingleMessage.displayName = "SingleMessage";
